import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Button } from 'primereact/button';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { Divider } from 'primereact/divider';
import { Dropdown } from 'primereact/dropdown';
import { InputMask } from 'primereact/inputmask';
import { InputNumber } from 'primereact/inputnumber';
import { InputSwitch } from 'primereact/inputswitch';
import { Message } from 'primereact/message';
import { AssignmentSchedule } from 'types/interfaces';
import { generateGUID } from 'utils';

import { TaskService } from 'components/services/TaskService';

import { normalizeScheduleTimeToHHMM } from '../utils/date-utils';

import './css/ScheduleCard.css';

interface PeriodTypeOption {
  label: string;
  value: number;
}

export type DepartmentAssignmentSchedule = AssignmentSchedule;

type ScheduleRow = {
  dayNumber: number | null;
  weekNumber: number | null;
  startTime: string;
  guid: string;
};

function isValidScheduleTime(time: string): boolean {
  if (!time || time.trim() === '') return false;
  return /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/.test(time);
}

function isCompleteScheduleRow(s: ScheduleRow): boolean {
  return (
    s.dayNumber !== null &&
    Number.isInteger(s.dayNumber) &&
    s.dayNumber >= 1 &&
    s.dayNumber <= 7 &&
    s.weekNumber !== null &&
    Number.isInteger(s.weekNumber) &&
    s.weekNumber >= -1 &&
    s.weekNumber <= 52 &&
    Boolean(s.startTime) &&
    isValidScheduleTime(s.startTime)
  );
}

function getDuplicateScheduleRowGuids(settings: ScheduleRow[]): Set<string> {
  const keyToGuids = new Map<string, string[]>();
  for (const s of settings) {
    if (!isCompleteScheduleRow(s)) continue;
    const key = `${s.dayNumber}|${s.weekNumber}|${normalizeScheduleTimeToHHMM(
      s.startTime
    )}`;
    const list = keyToGuids.get(key) ?? [];
    list.push(s.guid);
    keyToGuids.set(key, list);
  }
  const out = new Set<string>();
  keyToGuids.forEach((guids) => {
    if (guids.length > 1) {
      guids.forEach((g) => out.add(g));
    }
  });
  return out;
}

const DEFAULT_HEADER =
  'Настройка расписания запуска распределения нарядов по участкам';
const DEFAULT_SCHEDULE_ENABLED_LABEL = 'Распределение по расписанию';

async function fetchGlobalNextRunDateTime(): Promise<Date | null> {
  const response = await TaskService.getDepartmentAssignmentSchedule();
  if (response.data.isSuccess && response.data.value) {
    return new Date(response.data.value);
  }
  return null;
}

interface DepartmentAssignmentScheduleDialogProps {
  visible: boolean;
  onCancel: () => void;
  onSave: (data: DepartmentAssignmentSchedule | null, enabled: boolean) => void;
  departmentAssignmentSchedule: DepartmentAssignmentSchedule | null;
  header?: string;
  scheduleEnabledLabel?: string;
  enabled?: boolean;
  fetchNextRunDateTime?: () => Promise<Date | null>;
}

const DepartmentAssignmentScheduleDialog: React.FC<
  DepartmentAssignmentScheduleDialogProps
> = ({
  visible,
  onCancel,
  onSave,
  departmentAssignmentSchedule,
  header = DEFAULT_HEADER,
  scheduleEnabledLabel = DEFAULT_SCHEDULE_ENABLED_LABEL,
  enabled,
  fetchNextRunDateTime = fetchGlobalNextRunDateTime
}) => {
  // Сокращения, чтобы фраза согласовывалась при любом periodValue: «каждые 2 нед.»
  const periodTypes: PeriodTypeOption[] = [
    { label: 'дн.', value: 1 },
    { label: 'нед.', value: 2 },
    { label: 'мес.', value: 3 }
  ];

  const [scheduleEnabled, setScheduleEnabled] = useState(false);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [periodValue, setPeriodValue] = useState<number>(1);
  const [nextRunDateTime, setNextRunDateTime] = useState<Date | null>(null);
  const [timeErrors, setTimeErrors] = useState<Record<string, boolean>>({});
  const [daysLookbackError, setDaysLookbackError] = useState<boolean>(false);
  const [dayWeekErrors, setDayWeekErrors] = useState<Record<string, boolean>>(
    {}
  );
  const [periodType, setPeriodType] = useState<number>(1);
  const [daysLookback, setDaysLookback] = useState<number>(60);
  const [formError, setFormError] = useState<string | null>(null);
  const [scheduleSettings, setScheduleSettings] = useState<ScheduleRow[]>([
    { dayNumber: null, weekNumber: null, startTime: '', guid: generateGUID() }
  ]);

  const isValidDaysLookback = (value: number): boolean => {
    return value >= 1 && Number.isInteger(value);
  };

  useEffect(() => {
    if (scheduleEnabled) {
      setDaysLookbackError(!isValidDaysLookback(daysLookback));
    } else {
      setDaysLookbackError(false);
    }
  }, [daysLookback, scheduleEnabled]);

  const duplicateRowGuids = useMemo(() => {
    if (!scheduleEnabled) return new Set<string>();
    return getDuplicateScheduleRowGuids(scheduleSettings);
  }, [scheduleEnabled, scheduleSettings]);

  useEffect(() => {
    if (!visible) return;

    const hasSchedule = departmentAssignmentSchedule != null;
    setScheduleEnabled(enabled ?? hasSchedule);
    setFormError(null);
    setTimeErrors({});
    setDayWeekErrors({});

    if (hasSchedule && departmentAssignmentSchedule) {
      setScheduleSettings(
        departmentAssignmentSchedule.scheduleSettings.map((setting) => ({
          dayNumber: setting.dayNumber,
          weekNumber: setting.weekNumber,
          startTime: setting.startTime?.substring(0, 5) || '',
          guid: generateGUID()
        }))
      );
      setPeriodValue(departmentAssignmentSchedule.periodValue ?? 1);
      setPeriodType(departmentAssignmentSchedule.periodType ?? 1);
      setDaysLookback(departmentAssignmentSchedule.daysLookback ?? 1);
      setStartDate(
        departmentAssignmentSchedule.startDate
          ? new Date(departmentAssignmentSchedule.startDate)
          : null
      );
    } else {
      setScheduleSettings([
        {
          dayNumber: null,
          weekNumber: null,
          startTime: '',
          guid: generateGUID()
        }
      ]);
      setStartDate(null);
      setPeriodValue(1);
      setPeriodType(1);
    }
  }, [visible, departmentAssignmentSchedule, enabled]);

  const fetchNextRunTime = useCallback(async () => {
    if (!visible) return;

    try {
      setNextRunDateTime(await fetchNextRunDateTime());
    } catch (error) {
      console.error('Failed to fetch next run time:', error);
      setNextRunDateTime(null);
    }
  }, [visible, fetchNextRunDateTime]);

  useEffect(() => {
    if (visible) {
      fetchNextRunTime();
    }
  }, [visible, fetchNextRunTime]);

  useEffect(() => {
    if (duplicateRowGuids.size === 0 && formError?.includes('совпадающ')) {
      setFormError(null);
    }
  }, [duplicateRowGuids, formError]);

  const handleScheduleEnabledChange = (next: boolean) => {
    setScheduleEnabled(next);
    setFormError(null);
    if (!next) {
      setTimeErrors({});
      setDayWeekErrors({});
    }
    if (next && scheduleSettings.length === 0) {
      setScheduleSettings([
        {
          dayNumber: null,
          weekNumber: null,
          startTime: '',
          guid: generateGUID()
        }
      ]);
    }
  };

  const removeScheduleSetting = (guid: string) => {
    setScheduleSettings((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((setting) => setting.guid !== guid);
    });
  };

  const hasTimeFormatErrors = Object.keys(timeErrors).length > 0;

  const addScheduleSetting = () => {
    const newSetting: ScheduleRow = {
      dayNumber: null,
      weekNumber: null,
      startTime: '',
      guid: generateGUID()
    };
    setScheduleSettings((prev) => [...prev, newSetting]);
  };

  const updateScheduleSetting = (
    guid: string,
    field: string,
    value: unknown
  ) => {
    setScheduleSettings((prev) =>
      prev.map((setting) =>
        setting.guid === guid ? { ...setting, [field]: value } : setting
      )
    );

    if (field === 'startTime') {
      const str = (value as string) || '';
      if (str && !isValidScheduleTime(str)) {
        setTimeErrors((prev) => ({ ...prev, [guid]: true }));
      } else {
        setTimeErrors((prev) => {
          const next = { ...prev };
          delete next[guid];
          return next;
        });
      }
    }

    if (field === 'dayNumber' || field === 'weekNumber') {
      setDayWeekErrors((prev) => {
        const next = { ...prev };
        delete next[guid];
        return next;
      });
    }
  };

  const handleSave = () => {
    if (!scheduleEnabled) {
      onSave(null, false);
      onCancel();
      return;
    }

    setFormError(null);

    if (!startDate) {
      setFormError('Укажите дату начала расписания');
      return;
    }

    if (!daysLookback || daysLookback < 1 || !Number.isInteger(daysLookback)) {
      setFormError('Количество дней должно быть целым числом от 1 и более');
      return;
    }

    if (!scheduleSettings?.length) {
      setFormError('Добавьте хотя бы один день расписания');
      return;
    }

    const newTimeErrors: Record<string, boolean> = {};
    const newDayWeekErrors: Record<string, boolean> = {};
    let invalid = false;

    scheduleSettings.forEach((setting) => {
      const dayOk =
        setting.dayNumber !== null &&
        Number.isInteger(setting.dayNumber) &&
        setting.dayNumber >= 1 &&
        setting.dayNumber <= 7;
      const weekOk =
        setting.weekNumber !== null &&
        Number.isInteger(setting.weekNumber) &&
        setting.weekNumber >= -1 &&
        setting.weekNumber <= 52;
      if (!dayOk || !weekOk) {
        newDayWeekErrors[setting.guid] = true;
        invalid = true;
      }
      if (!setting.startTime || !isValidScheduleTime(setting.startTime)) {
        newTimeErrors[setting.guid] = true;
        invalid = true;
      }
    });

    if (invalid) {
      setTimeErrors(newTimeErrors);
      setDayWeekErrors(newDayWeekErrors);
      return;
    }

    const seen = new Set<string>();
    for (const s of scheduleSettings) {
      const key = `${s.dayNumber}|${s.weekNumber}|${normalizeScheduleTimeToHHMM(
        s.startTime
      )}`;
      if (seen.has(key)) {
        setFormError(
          'Не допускаются полностью совпадающие строки (номер дня, номер недели, время запуска)'
        );
        return;
      }
      seen.add(key);
    }

    const formattedDate = `${startDate.getFullYear()}-${String(
      startDate.getMonth() + 1
    ).padStart(2, '0')}-${String(startDate.getDate()).padStart(2, '0')}`;

    const saveData: DepartmentAssignmentSchedule = {
      startDate: formattedDate,
      periodValue,
      periodType,
      daysLookback,
      scheduleSettings: scheduleSettings.map((row) => ({
        dayNumber: row.dayNumber as number,
        weekNumber: row.weekNumber as number,
        startTime: `${row.startTime}:00`
      }))
    };
    onSave(saveData, true);
    onCancel();
  };

  const footerContent = (
    <div>
      <div className="flex gap-3">
        <Button
          label="Применить"
          icon="pi pi-check"
          onClick={handleSave}
          disabled={
            scheduleEnabled && (hasTimeFormatErrors || daysLookbackError)
          }
        />
        <Button
          label="Отмена"
          icon="pi pi-times"
          onClick={onCancel}
          className="p-button-secondary"
        />
      </div>
    </div>
  );

  return (
    <Dialog
      visible={visible}
      onHide={onCancel}
      header={header}
      style={{ minWidth: '1050px' }}
      modal
      footer={footerContent}
    >
      <Divider />
      <div>
        {scheduleEnabled && nextRunDateTime && (
          <div className="next-assignment-info mb-3">
            Следующее распределение согласно расписанию произойдет{' '}
            <strong>
              {nextRunDateTime.toLocaleDateString('ru-RU', {
                day: 'numeric',
                month: 'long',
                year: 'numeric'
              })}
            </strong>{' '}
            в{' '}
            <strong>
              {nextRunDateTime.toLocaleTimeString('ru-RU', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </strong>
          </div>
        )}
        <div className="flex flex-row gap-5 mb-4 align-items-center">
          <label
            htmlFor="dept-assign-schedule-enabled"
            className="p-0 font-medium white-space-nowrap"
          >
            {scheduleEnabledLabel}
          </label>
          <InputSwitch
            id="dept-assign-schedule-enabled"
            checked={scheduleEnabled}
            onChange={(e) => handleScheduleEnabledChange(Boolean(e.value))}
          />
        </div>

        {formError ? (
          <Message severity="error" text={formError} className="w-full mb-3" />
        ) : null}

        {scheduleEnabled ? (
          <>
            <div className="flex flex-row gap-5 mb-4">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center w-full">
                  <label
                    htmlFor="startDate"
                    className="schedule-card__label p-0"
                  >
                    Дата начала<span className="required">*</span>:
                  </label>
                  <div className="flex gap-2 schedule-card__input w-full">
                    <div style={{ width: 224 }}>
                      <Calendar
                        id="startDate"
                        value={startDate}
                        onChange={(e) =>
                          setStartDate((e.value as Date) ?? null)
                        }
                        dateFormat="dd.mm.yy"
                        showIcon
                        showButtonBar
                        locale="ru"
                        placeholder="Выберите дату"
                        className="w-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-5 mb-4">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center w-full">
                  <label className="schedule-card__label p-0">
                    Распределять наряды за последние
                    <span className="required">*</span>:
                  </label>
                  <div className="flex gap-2 schedule-card__input w-full">
                    <div>
                      <InputNumber
                        value={daysLookback}
                        onValueChange={(e) => setDaysLookback(e.value ?? 60)}
                        min={1}
                        showButtons
                        useGrouping={false}
                        className={daysLookbackError ? 'p-invalid' : ''}
                      />
                    </div>
                    <div className="flex align-items-center">дней</div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex flex-row gap-5 mb-4">
              <div className="w-full">
                <div className="flex flex-row justify-content-between align-items-center w-full">
                  <label
                    htmlFor="periodValue"
                    className="schedule-card__label p-0"
                  >
                    Повторять каждые<span className="required">*</span>:
                  </label>
                  <div className="flex gap-2 schedule-card__input w-full">
                    <div>
                      <InputNumber
                        value={periodValue}
                        min={1}
                        showButtons
                        onValueChange={(e) => setPeriodValue(e.value ?? 1)}
                      />
                    </div>
                    <div>
                      <Dropdown
                        value={periodType}
                        options={periodTypes}
                        onChange={(e) => setPeriodType(e.value)}
                        optionLabel="label"
                        optionValue="value"
                        style={{ width: 110 }}
                        placeholder="период повторения"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="schedule-days-container">
              <div className="schedule-day-header">
                <div className="schedule-day-column">
                  Номер дня<span className="required">*</span>
                </div>
                <div className="schedule-day-column">
                  Номер недели<span className="required">*</span>
                </div>
                <div className="schedule-day-column">Время запуска (ЧЧ:ММ)</div>
                <div className="schedule-day-column-actions"></div>
              </div>
              <div
                className="scrollable-container"
                id="tags-scroll"
                style={{
                  width: '100%',
                  height: 'fit-content',
                  maxHeight: '350px',
                  overflow: 'auto'
                }}
              >
                <div
                  className="flex flex-column scrollable-content"
                  style={{ width: '99%' }}
                >
                  {scheduleSettings.map((setting) => {
                    const isDup = duplicateRowGuids.has(setting.guid);
                    const dupClass = isDup ? ' schedule-dayItem-duplicate' : '';
                    return (
                      <div
                        key={setting.guid}
                        className={`schedule-day-row p-fluid${
                          isDup ? ' schedule-day-row--duplicate' : ''
                        }`}
                      >
                        <div
                          className={`p-inputgroup${
                            dayWeekErrors[setting.guid]
                              ? ' schedule-dayItem-error'
                              : ''
                          }${dupClass}`}
                        >
                          <span className="p-inputgroup-addon">
                            <i className="pi pi-calendar"></i>
                          </span>
                          <InputNumber
                            value={setting.dayNumber}
                            onValueChange={(e) =>
                              updateScheduleSetting(
                                setting.guid,
                                'dayNumber',
                                e.value ?? null
                              )
                            }
                            min={1}
                            max={7}
                            showButtons
                            className="compact-input"
                            useGrouping={false}
                          />
                        </div>

                        <div
                          className={`p-inputgroup${
                            dayWeekErrors[setting.guid]
                              ? ' schedule-dayItem-error'
                              : ''
                          }${dupClass}`}
                        >
                          <span className="p-inputgroup-addon">
                            <i className="pi pi-calendar-plus"></i>
                          </span>
                          <InputNumber
                            value={setting.weekNumber}
                            onValueChange={(e) =>
                              updateScheduleSetting(
                                setting.guid,
                                'weekNumber',
                                e.value ?? null
                              )
                            }
                            min={-1}
                            max={52}
                            showButtons
                            className="compact-input"
                            useGrouping={false}
                          />
                        </div>

                        <div
                          className={`p-inputgroup${
                            timeErrors[setting.guid]
                              ? ' schedule-dayItem-error'
                              : ''
                          }${dupClass}`}
                        >
                          <span className="p-inputgroup-addon">
                            <i className="pi pi-clock"></i>
                          </span>
                          <InputMask
                            mask="99:99"
                            value={setting.startTime}
                            onChange={(e) =>
                              updateScheduleSetting(
                                setting.guid,
                                'startTime',
                                e.value || ''
                              )
                            }
                            placeholder="ЧЧ:ММ"
                            slotChar="ЧЧ:ММ"
                            className="compact-input"
                          />
                        </div>

                        <Button
                          icon="pi pi-trash"
                          className="p-button-rounded p-button-danger p-button-text"
                          onClick={() => removeScheduleSetting(setting.guid)}
                          disabled={scheduleSettings.length <= 1}
                          tooltip="Удалить день"
                          tooltipOptions={{ position: 'top' }}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
              <Button
                icon="pi pi-plus"
                label="Добавить день"
                className="p-button-outlined mt-2"
                onClick={addScheduleSetting}
              />
            </div>
          </>
        ) : null}
      </div>
    </Dialog>
  );
};

export default DepartmentAssignmentScheduleDialog;
